// cartStorage.js - Утилиты для сохранения корзины в localStorage

import botAPI from './botAPI.js';
import { Platform } from './utils/platform';
import { logger } from './utils/logger.js';

const CART_STORAGE_KEY = 'pathoffish_cart_global'; // Один глобальный ключ корзины
const STORAGE_VERSION = '3.0'; // v3.0 - глобальные циклы без district_id
const MAX_STORAGE_AGE_DAYS = 7; // Максимальный срок хранения данных

export const CartStorage = {

  // Сохранить корзину (глобальная - без района)
  saveCart: (cartItems) => {
    try {
      const userId = Platform.getUser()?.id || null;

      // Сохраняем в localStorage
      if (!cartItems || cartItems.length === 0) {
        localStorage.removeItem(CART_STORAGE_KEY);
        logger.log('🗑️ Корзина очищена в localStorage');

        // ВАЖНО: Синхронизация пустой корзины с БД управляется из App.jsx
        return;
      }

      const cartData = {
        version: STORAGE_VERSION,
        items: cartItems,
        timestamp: new Date().toISOString(),
        userId: userId
      };

      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
      logger.log('💾 Корзина сохранена в localStorage:', cartItems.length, 'товаров');

      // ВАЖНО: Синхронизация с БД теперь управляется из App.jsx (useEffect с debounce)
      // Здесь НЕ вызываем botAPI.saveCartSnapshot чтобы избежать race condition
    } catch (error) {
      console.error('❌ Ошибка сохранения корзины:', error);
    }
  },

  // Загрузить корзину (глобальная - без района)
  loadCart: () => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (!stored) return null;

      const cartData = JSON.parse(stored);

      // Проверяем версию
      if (cartData.version !== STORAGE_VERSION) {
        logger.log('🔄 Устаревшая версия корзины, очищаем');
        localStorage.removeItem(CART_STORAGE_KEY);
        return null;
      }

      // Проверяем срок хранения
      const storedDate = new Date(cartData.timestamp);
      const now = new Date();
      const daysDiff = (now - storedDate) / (1000 * 60 * 60 * 24);

      if (daysDiff > MAX_STORAGE_AGE_DAYS) {
        logger.log('🗑️ Корзина устарела, очищаем');
        localStorage.removeItem(CART_STORAGE_KEY);
        return null;
      }

      // Проверяем пользователя (опционально)
      const currentUserId = Platform.getUser()?.id;
      if (currentUserId && cartData.userId && currentUserId !== cartData.userId) {
        logger.log('👤 Корзина другого пользователя, очищаем');
        localStorage.removeItem(CART_STORAGE_KEY);
        return null;
      }

      logger.log('📦 Корзина загружена из localStorage:', cartData.items.length, 'товаров');
      return cartData.items;
    } catch (error) {
      console.error('❌ Ошибка загрузки корзины:', error);
      localStorage.removeItem(CART_STORAGE_KEY);
      return null;
    }
  },

  // Очистить корзину (глобальная - без района)
  clearCart: () => {
    localStorage.removeItem(CART_STORAGE_KEY);
    logger.log('🗑️ Корзина очищена');

    // Фоновая синхронизация - отправляем пустую корзину
    const userId = Platform.getUser()?.id || null;
    if (userId) {
      botAPI.saveCartSnapshot(userId, null, [])
        .catch(err => console.warn('⚠️ Не удалось синхронизировать очистку корзины:', err));
    }
  },

  // Получить количество товаров в корзине (глобальная)
  getCartItemsCount: () => {
    const items = CartStorage.loadCart();
    return items ? items.reduce((sum, item) => sum + item.quantity, 0) : 0;
  },

  // Очистить все данные (глобальная корзина)
  clearAll: () => {
    localStorage.removeItem(CART_STORAGE_KEY);
    logger.log('🗑️ Все данные очищены');
  },

  // Мигрировать старые корзины на новую структуру (v3.0 - глобальный цикл)
  migrateOldCarts: () => {
    // Проверяем старые ключи с префиксом района
    const oldPrefixes = ['pathoffish_cart_', 'pathoffish_cart'];
    let migrated = false;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Проверяем старые форматы
      const isOldFormat = oldPrefixes.some(prefix => key.startsWith(prefix));
      if (isOldFormat && key !== CART_STORAGE_KEY) {
        try {
          const oldCart = localStorage.getItem(key);
          if (oldCart) {
            const cartData = JSON.parse(oldCart);
            if (cartData.items && cartData.items.length > 0) {
              // Мигрируем в глобальную корзину (берём первую найденную)
              if (!migrated) {
                CartStorage.saveCart(cartData.items);
                logger.log('📦 Мигрирована старая корзина:', cartData.items.length, 'товаров');
                migrated = true;
              }
            }
          }
          localStorage.removeItem(key);
        } catch (e) {
          localStorage.removeItem(key);
        }
      }
    }
  },

  /**
   * Загрузить корзину с fallback на БД (глобальная - без района)
   * Если localStorage пуст → попытаться загрузить из БД
   *
   * @param {number|null} userId - Telegram ID пользователя (для загрузки из БД)
   * @returns {Promise<Object>} { items: Array, source: 'localStorage' | 'database' | null }
   */
  loadCartWithDBFallback: async (userId = null) => {
    try {
      // 1. Попытка загрузить из localStorage
      const localCart = CartStorage.loadCart();

      if (localCart && localCart.length > 0) {
        logger.log('📦 Корзина загружена из localStorage:', localCart.length, 'товаров');
        return { items: localCart, source: 'localStorage' };
      }

      // 2. Если localStorage пуст и есть userId → пробуем БД
      if (!userId) {
        logger.log('ℹ️ Корзина пуста, userId не указан');
        return { items: null, source: null };
      }

      logger.log('🔄 localStorage пуст, пробуем загрузить из БД...');

      const response = await botAPI.getCartSnapshot(userId);

      if (!response?.success || !response?.snapshot) {
        logger.log('ℹ️ Snapshot корзины не найден в БД');
        return { items: null, source: null };
      }

      const snapshot = response.snapshot;

      // Проверяем что есть товары
      if (!snapshot.cart_items || snapshot.cart_items.length === 0) {
        logger.log('ℹ️ Snapshot пуст');
        return { items: null, source: null };
      }

      // Преобразуем формат из БД в формат приложения
      const restoredItems = snapshot.cart_items.map(item => ({
        id: item.product_id,
        product_id: item.product_id,
        name: item.name,
        product_name: item.name,
        price: item.price,
        current_price: item.price,
        quantity: item.quantity,
        image_url: item.image_url
      }));

      // Сохраняем в localStorage для последующих запусков
      CartStorage.saveCart(restoredItems);

      logger.log('✅ Корзина восстановлена из БД:', restoredItems.length, 'товаров');
      return { items: restoredItems, source: 'database' };

    } catch (error) {
      console.error('❌ Ошибка загрузки корзины с fallback:', error);
      return { items: null, source: null };
    }
  }
};

export default CartStorage;